/**
 * RfhqCatalogFilters — native filter UI for RfhqProductGrid's ecosystem.
 *
 * Replaces core webshop's `#product-filters` sidebar (checkboxes rendered
 * by field_filter_section/attribute_filter_section Jinja macros, augmented
 * at runtime by raftor_webhq's catalog_facets.js for price/offers/badges) on
 * both /all-products and Item Group pages, which share one JS file
 * (all-products/index.js) and one ProductView class.
 *
 * DELIBERATELY DOES NOT reimplement re-fetching or grid reconstruction -
 * that cycle (toolbar, sort, pagination, search box, all core's own
 * views.js ProductView.make()) already works and has no other caller
 * anywhere in the codebase, so it stays. This component's only job: render
 * every filter control itself, keep field_filters/attribute_filters in the
 * URL in the exact shape core already reads, and call
 * window.__raftorProductView.make(true) (already globally exposed by
 * catalog_search_enter.js) to trigger that existing cycle.
 *
 * Root container must never use id="product-filters" or the
 * .product-filter/.field-filter/.attribute-filter/.discount-filter classes
 * core's views.js and catalog_facets.js target by literal selector - doing
 * so is what makes both of those become fully inert on pages that mount
 * this component, with no patches to either file.
 */

import { el } from '../dom.js';
import { fetchFacets } from './facets_source.js';

function parseJsonParam(raw) {
	if (!raw) return {};
	try {
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch (err) {
		return {};
	}
}

function toArray(value) {
	if (value == null) return [];
	return Array.isArray(value) ? value.slice() : [value];
}

function clampNum(value, min, max) {
	const n = Number(value);
	if (Number.isNaN(n)) return min;
	return Math.min(Math.max(n, min), max);
}

export class RfhqCatalogFilters {
	/**
	 * @param {object} opts
	 * @param {HTMLElement} opts.el
	 * @param {'vertical'|'horizontal'} [opts.layout='vertical']
	 * @param {string} [opts.itemGroup]
	 */
	constructor(opts) {
		this.el = opts.el;
		this.layout = opts.layout || 'vertical';
		this.itemGroup = opts.itemGroup || null;
		// A list an app feeds (not the shop's catalogue) brings its own facets: [{name, label, options: [{value, label, count}]} | {name, label, toggle}].
		// A choice is then a plain value in the address (name=a,b) handed to onChange, instead of the shop's field_filters.
		this.appFacets = opts.facets || null;
		this.onChange = opts.onChange || null;

		this.fieldFilters = {};
		this.attributeFilters = {};
		this._restoreFromUrl();
	}

	/** Single source of truth for pre-checked state on load - replaces both
	 * core's own restore_filters_state() and catalog_facets.js's separate
	 * restoration for its injected facets. */
	_restoreFromUrl() {
		const qp = (typeof frappe !== 'undefined' && frappe.utils && frappe.utils.get_query_params)
			? frappe.utils.get_query_params()
			: Object.fromEntries(new URLSearchParams(window.location.search));

		if (this.appFacets) {
			this.fieldFilters = {};
			this.appFacets.forEach((facet) => {
				const values = String(qp[facet.name] || '').split(',').map((v) => v.trim()).filter(Boolean);
				if (values.length) this.fieldFilters[facet.name] = values;
			});
			return;
		}
		this.fieldFilters = parseJsonParam(qp.field_filters);
		this.attributeFilters = parseJsonParam(qp.attribute_filters);
	}

	async render() {
		this.el.classList.add('rfhq-catalog-filters', `rfhq-catalog-filters--${this.layout}`);
		// A choice draws the panel again; the dropdown being used stays open so several values can be ticked one after another.
		const wasOpen = new Set([...this.el.querySelectorAll('details[open] > summary')].map((s) => s.textContent));
		this.el.innerHTML = '';

		const facets = this.appFacets ? {} : await fetchFacets({
			itemGroup: this.itemGroup,
			queryArgs: {
				item_group: this.itemGroup || undefined,
				field_filters: this.fieldFilters,
				attribute_filters: this.attributeFilters,
			},
		});

		const blocks = [];
		(this.appFacets || []).forEach((facet) => {
			if (facet.toggle) blocks.push(this._renderToggleBlock(facet));
			else if ((facet.display === 'tabs' || facet.display === 'tiles') && (facet.options || []).length) blocks.push(this._renderTabsBlock(facet));
			else if (facet.display === 'chips' && (facet.options || []).length) blocks.push(this._renderChipsBlock(facet));
			else if ((facet.options || []).length) blocks.push(this._renderCheckboxBlock(facet.label, facet.name, facet.options, (r) => r.label, { valueKey: 'value' }));
		});
		this.chipsOnly = Boolean(this.appFacets) && this.appFacets.every((f) => f.display === 'chips' || f.display === 'tabs' || f.display === 'tiles' || f.toggle || !(f.options || []).length && !f.toggle);
		if (!this.itemGroup && !this.groupTiles && (facets.item_groups || []).length) {
			blocks.push(this._renderCheckboxBlock('Item Group', 'item_group', facets.item_groups, (r) => r.item_group_name));
		}
		if ((facets.brands || []).length) {
			blocks.push(this._renderCheckboxBlock('Brand', 'brand', facets.brands, (r) => r.brand, {
				searchable: facets.brands_has_more,
			}));
		}
		if ((facets.price_ranges || []).length || facets.price_min_max) {
			blocks.push(this._renderPriceBlock(facets.price_ranges || [], facets.price_min_max || {}));
		}
		if ((facets.offers || []).length) {
			blocks.push(this._renderCheckboxBlock('Offers', 'offers', facets.offers, (r) => r.label, { valueKey: 'code' }));
		}
		if ((facets.badges || []).length) {
			blocks.push(this._renderCheckboxBlock('Badges', 'badges', facets.badges, (r) => r.label, { valueKey: 'code' }));
		}
		(facets.attributes || []).forEach((attr) => {
			blocks.push(this._renderAttributeBlock(attr));
		});
		if ((facets.discount_filters || []).length) {
			blocks.push(this._renderDiscountBlock(facets.discount_filters));
		}

		const hasActive = Object.keys(this.fieldFilters).length || Object.keys(this.attributeFilters).length;
		const header = el('div', { class: 'rfhq-filters-header' }, [
			el('span', { class: 'rfhq-filters-title', text: __('Filters') }),
			hasActive
				? el('button', { type: 'button', class: 'rfhq-filters-clear', text: __('Clear All') })
				: null,
		]);

		this.el.classList.toggle('rfhq-catalog-filters--chips-only', this.chipsOnly);
		if (!this.chipsOnly) this.el.appendChild(header);  // buttons on their own need no "Filters" title or drawer
		blocks.forEach((b) => {
			const label = b.querySelector && b.querySelector('summary');
			if (label && wasOpen.has(label.textContent)) b.setAttribute('open', '');
			this.el.appendChild(b);
		});

		if (!this._clearBound) {  // once: the panel is drawn again after every choice, and each draw would add another handler
			this._clearBound = true;
			this.el.addEventListener('click', (e) => {
				if (e.target.closest('.rfhq-filters-clear')) this._clearAll();
			});
		}

		if (!this.chipsOnly) this._setupMobileOffcanvas();
	}

	/* ── a facet drawn as a row of buttons that slides sideways: one choice at a time, chosen again to clear ───────── */

	/** One of a few ways to look at a list, drawn as tabs: always one chosen (the one in the address, else the feed's `selected`, else the first). */
	_renderTabsBlock(facet) {
		const current = toArray(this.fieldFilters[facet.name])[0] || facet.selected || facet.options[0].value;
		const tiles = facet.display === 'tiles';
		const mark = (o) => {  // an icon the feed named, as the markup the server drew for it (never markup of the app's own)
			if (!tiles || !o.icon) return null;
			try { return el('span', { class: 'rfhq-tab__mark' }, [document.importNode(new DOMParser().parseFromString(o.icon, 'image/svg+xml').documentElement, true)]); } catch (err) { return null; }
		};
		const strip = el('div', { class: tiles ? 'rfhq-tabs rfhq-tabs--tiles' : 'rfhq-tabs', role: 'tablist', 'aria-label': facet.label || '' },
			facet.options.map((o) => el('button', { type: 'button', role: 'tab', class: 'rfhq-tab', dataset: { filterKey: facet.name, value: o.value }, 'aria-selected': String(current === o.value) },
				[mark(o), el('span', { class: 'rfhq-tab__label', text: o.label })])));
		strip.addEventListener('click', (e) => {
			const button = e.target.closest('.rfhq-tab');
			if (!button || button.dataset.value === current) return;
			this.fieldFilters[facet.name] = [button.dataset.value];
			(facet.resets || []).forEach((name) => { delete this.fieldFilters[name]; });  // what was narrowed inside the old tab no longer applies
			this._writeUrlAndRefresh();
		});
		return el('div', { class: 'rfhq-filter-block rfhq-filter-block--tabs' }, [strip]);
	}

	_renderChipsBlock(facet) {
		const current = toArray(this.fieldFilters[facet.name])[0] || '';
		const chip = (value, label, count) => el('button', {
			type: 'button', class: 'rfhq-chip', dataset: { filterKey: facet.name, value }, 'aria-pressed': String(current === value),
		}, [label, count != null ? el('span', { class: 'rfhq-chip-count', text: String(count) }) : null]);
		const strip = el('div', { class: 'rfhq-chips', role: 'group', 'aria-label': facet.label || '' },
			[facet.any_label ? chip('', facet.any_label, null) : null, ...facet.options.map((o) => chip(o.value, o.label, o.count))]);  // the word for "any" comes with the feed's facet
		strip.addEventListener('click', (e) => {
			const button = e.target.closest('.rfhq-chip');
			if (!button) return;
			const value = button.dataset.value;
			if (!value || value === current) delete this.fieldFilters[facet.name];
			else this.fieldFilters[facet.name] = [value];
			this._writeUrlAndRefresh();
		});
		return el('div', { class: 'rfhq-filter-block rfhq-filter-block--chips' }, [
			facet.heading ? el('div', { class: 'rfhq-chips-heading', role: 'heading', 'aria-level': '2', text: facet.heading }) : null,
			strip,
		]);
	}

	/* ── generic checkbox facet (item group / brand / offers / badges) ──── */

	_renderCheckboxBlock(label, filterKey, rows, labelOf, opts) {
		const options = opts || {};
		const valueKey = options.valueKey || null;
		const activeValues = new Set(toArray(this.fieldFilters[filterKey]));

		const optionEls = rows.map((row) => {
			const value = valueKey ? row[valueKey] : labelOf(row);
			const checked = activeValues.has(value);
			const input = el('input', {
				type: 'checkbox',
				class: 'rfhq-filter-checkbox',
				dataset: { filterKey, value },
				checked: checked || null,
			});
			return el('label', { class: 'rfhq-filter-option' }, [
				input,
				el('span', { class: 'rfhq-filter-option-label', text: `${labelOf(row)} (${row.count})` }),
			]);
		});

		const block = this._block(label, optionEls);

		if (options.searchable) {
			const search = el('input', {
				type: 'search',
				class: 'rfhq-filter-search',
				placeholder: __('Search {0}...', [label]),
			});
			search.addEventListener('input', () => {
				const q = search.value.trim().toLowerCase();
				optionEls.forEach((row) => {
					const text = row.querySelector('.rfhq-filter-option-label').textContent.toLowerCase();
					row.style.display = !q || text.includes(q) ? '' : 'none';
				});
			});
			block.insertBefore(search, block.firstElementChild.nextSibling);
		}

		block.addEventListener('change', (e) => {
			const input = e.target.closest('.rfhq-filter-checkbox');
			if (!input) return;
			const key = input.dataset.filterKey;
			const value = input.dataset.value;
			const current = new Set(toArray(this.fieldFilters[key]));
			if (input.checked) current.add(value);
			else current.delete(value);
			if (current.size) this.fieldFilters[key] = Array.from(current);
			else delete this.fieldFilters[key];
			this._writeUrlAndRefresh();
		});

		return block;
	}

	/* ── a yes/no facet an app offers (for example "Online") ─────────────── */

	_renderToggleBlock(facet) {
		const input = el('input', { type: 'checkbox', class: 'rfhq-filter-checkbox', dataset: { filterKey: facet.name, value: '1' }, checked: toArray(this.fieldFilters[facet.name]).length || null });
		const block = el('div', { class: 'rfhq-filter-block rfhq-filter-block--toggle' }, [
			el('label', { class: 'rfhq-filter-option' }, [input, el('span', { class: 'rfhq-filter-option-label', text: facet.label })]),
		]);
		input.addEventListener('change', () => {
			if (input.checked) this.fieldFilters[facet.name] = ['1'];
			else delete this.fieldFilters[facet.name];
			this._writeUrlAndRefresh();
		});
		return block;
	}

	/* ── attribute facet (Size, Colour, ...) ─────────────────────────────── */

	_renderAttributeBlock(attr) {
		const activeValues = new Set(toArray(this.attributeFilters[attr.name]));

		const optionEls = attr.values.map((v) => {
			const checked = activeValues.has(v.value);
			const input = el('input', {
				type: 'checkbox',
				class: 'rfhq-filter-attribute',
				dataset: { attribute: attr.name, value: v.value },
				checked: checked || null,
			});
			return el('label', { class: 'rfhq-filter-option' }, [
				input,
				el('span', { class: 'rfhq-filter-option-label', text: `${v.value} (${v.count})` }),
			]);
		});

		const block = this._block(attr.name, optionEls);
		block.addEventListener('change', (e) => {
			const input = e.target.closest('.rfhq-filter-attribute');
			if (!input) return;
			const attribute = input.dataset.attribute;
			const value = input.dataset.value;
			const current = new Set(toArray(this.attributeFilters[attribute]));
			if (input.checked) current.add(value);
			else current.delete(value);
			if (current.size) this.attributeFilters[attribute] = Array.from(current);
			else delete this.attributeFilters[attribute];
			this._writeUrlAndRefresh();
		});

		return block;
	}

	/* ── discount facet (single-select "X% and below") ───────────────────── */

	_renderDiscountBlock(buckets) {
		const active = toArray(this.fieldFilters.discount)[0];

		const optionEls = buckets.map((bucket) => {
			const input = el('input', {
				type: 'radio',
				name: 'rfhq-discount-filter',
				class: 'rfhq-filter-discount',
				dataset: { value: bucket.threshold },
				checked: String(active) === String(bucket.threshold) || null,
			});
			return el('label', { class: 'rfhq-filter-option' }, [
				input,
				el('span', { class: 'rfhq-filter-option-label', text: `${bucket.label} (${bucket.count})` }),
			]);
		});

		const block = this._block(__('Discount'), optionEls);
		block.addEventListener('change', (e) => {
			const input = e.target.closest('.rfhq-filter-discount');
			if (!input) return;
			this.fieldFilters.discount = [input.dataset.value];
			this._writeUrlAndRefresh();
		});

		return block;
	}

	/* ── price range (dual slider) ───────────────────────────────────────────
	   Two overlapping native <input type="range"> on one track, each with a
	   transparent, pointer-events:none track and a pointer-events:auto thumb
	   (rfhq_catalog_filters.css) - the standard dual-slider technique. Each
	   handle stays independently draggable with no z-index/click-stealing
	   fight between them, and no drag-tracking JS of our own is needed since
	   the browser's native range input already does that. minMax.symbol
	   (raftor_webhq's _get_price_min_max) is the site's real configured
	   currency symbol - this is a pure client-side JS component with no
	   template pass to pull one from otherwise, so it has to travel in the
	   same facet payload the bounds already do (was hardcoded nowhere before;
	   simply absent, which read as USD to anyone assuming the $-prefixed
	   Catalog Price Range labels beside it were the currency). */

	_renderPriceBlock(ranges, minMax) {
		const min = minMax.min != null ? Math.floor(minMax.min) : 0;
		const max = minMax.max != null ? Math.ceil(minMax.max) : min;
		const symbol = minMax.symbol || '';
		const activeFrom = clampNum(toArray(this.fieldFilters.price_from)[0] || min, min, max);
		const activeTo = clampNum(toArray(this.fieldFilters.price_to)[0] || max, min, max);

		const minRange = el('input', {
			type: 'range', class: 'rfhq-price-range rfhq-price-range--min',
			min, max, value: activeFrom, 'aria-label': __('Minimum price'),
		});
		const maxRange = el('input', {
			type: 'range', class: 'rfhq-price-range rfhq-price-range--max',
			min, max, value: activeTo, 'aria-label': __('Maximum price'),
		});
		const fill = el('div', { class: 'rfhq-price-slider-fill' });
		// Only rendered when the dataset actually spans a range - a single-
		// price catalog (min === max) has nothing for two handles to select
		// between, and a zero-width slider is just a confusing flat line.
		const slider = min < max
			? el('div', { class: 'rfhq-price-slider' }, [
				el('div', { class: 'rfhq-price-slider-track' }), fill, minRange, maxRange,
			])
			: null;

		const minInput = el('input', { type: 'number', class: 'rfhq-price-input', value: activeFrom, placeholder: 'Min' });
		const maxInput = el('input', { type: 'number', class: 'rfhq-price-input', value: activeTo, placeholder: 'Max' });
		const applyBtn = el('button', { type: 'button', class: 'rfhq-price-apply', text: __('Apply') });

		const content = [
			slider,
			el('div', { class: 'rfhq-price-inputs' }, [
				el('div', { class: 'rfhq-price-input-group' }, [
					symbol ? el('span', { class: 'rfhq-price-symbol', text: symbol }) : null,
					minInput,
				]),
				el('div', { class: 'rfhq-price-input-group' }, [
					symbol ? el('span', { class: 'rfhq-price-symbol', text: symbol }) : null,
					maxInput,
				]),
				applyBtn,
			]),
		];

		if (ranges.length) {
			const rangeEls = ranges.map((r) =>
				el('label', { class: 'rfhq-filter-option' }, [
					el('input', {
						type: 'checkbox',
						class: 'rfhq-filter-price-range',
						dataset: { from: r.from_amount || '', to: r.to_amount || '' },
					}),
					el('span', { class: 'rfhq-filter-option-label', text: `${r.label} (${r.count})` }),
				])
			);
			content.push(...rangeEls);
		}

		const block = this._block(__('Price'), content);

		const updateFill = () => {
			if (!slider || max === min) return;
			const lo = (Number(minRange.value) - min) / (max - min) * 100;
			const hi = (Number(maxRange.value) - min) / (max - min) * 100;
			fill.style.left = `${lo}%`;
			fill.style.right = `${100 - hi}%`;
		};

		const applyFrom = (value) => {
			const from = clampNum(value, min, Number(maxRange ? maxRange.value : max));
			if (from > min) this.fieldFilters.price_from = [from];
			else delete this.fieldFilters.price_from;
			return from;
		};
		const applyTo = (value) => {
			const to = clampNum(value, Number(minRange ? minRange.value : min), max);
			if (to < max) this.fieldFilters.price_to = [to];
			else delete this.fieldFilters.price_to;
			return to;
		};

		if (slider) {
			// Two handles on one track can cross - clamping each against the
			// other's current value on every input (not just on release)
			// keeps "min" always <= "max" instead of letting them swap past
			// each other, which is what makes a dual-range slider read as
			// two independent handles rather than one confusing one.
			minRange.addEventListener('input', () => {
				if (Number(minRange.value) > Number(maxRange.value)) minRange.value = maxRange.value;
				minInput.value = minRange.value;
				updateFill();
			});
			maxRange.addEventListener('input', () => {
				if (Number(maxRange.value) < Number(minRange.value)) maxRange.value = minRange.value;
				maxInput.value = maxRange.value;
				updateFill();
			});
			// 'change' (fires on release, not per-pixel while dragging) is
			// the natural "confirm" gesture - same live-apply behaviour the
			// range-bucket checkboxes below already have on their own change.
			minRange.addEventListener('change', () => { applyFrom(minRange.value); this._writeUrlAndRefresh(); });
			maxRange.addEventListener('change', () => { applyTo(maxRange.value); this._writeUrlAndRefresh(); });
			updateFill();
		}

		applyBtn.addEventListener('click', () => {
			const from = applyFrom(minInput.value || min);
			const to = applyTo(maxInput.value || max);
			minInput.value = from;
			maxInput.value = to;
			if (slider) { minRange.value = from; maxRange.value = to; updateFill(); }
			this._writeUrlAndRefresh();
		});

		block.addEventListener('change', (e) => {
			const input = e.target.closest('.rfhq-filter-price-range');
			if (!input) return;
			if (input.dataset.from) this.fieldFilters.price_from = [Number(input.dataset.from)];
			if (input.dataset.to) this.fieldFilters.price_to = [Number(input.dataset.to)];
			this._writeUrlAndRefresh();
		});

		return block;
	}

	/* ── shared block shell (collapsible <details> in both layouts - open by
	   default in vertical, closed by default in horizontal) ─────────────── */

	_block(label, children) {
		return el('details', { class: 'rfhq-filter-block', open: this.layout === 'vertical' || null }, [
			el('summary', { class: 'rfhq-filter-label', text: label }),
			el('div', { class: 'rfhq-filter-options' }, children),
		]);
	}

	/* ── URL sync + refresh trigger ───────────────────────────────────────── */

	_writeUrlAndRefresh() {
		const url = new URL(window.location.href);
		const search = url.searchParams;

		if (this.appFacets) {
			const chosen = {};
			this.appFacets.forEach((facet) => {
				const values = toArray(this.fieldFilters[facet.name]);
				if (values.length) { search.set(facet.name, values.join(',')); chosen[facet.name] = values.join(','); } else search.delete(facet.name);
			});
			const text = search.toString();
			window.history.pushState('filters', '', url.pathname + (text ? '?' + text : ''));
			if (this.onChange) this.onChange(chosen);
			window.dispatchEvent(new CustomEvent('rfhq:app-filters', { detail: { chosen, facets: this.appFacets.map((f) => f.name), origin: this.el } }));
			this.render();
			this._closeMobileOffcanvas();
			return;
		}

		if (this.groupTiles) {  // the group is chosen in the tiles over the listing: this panel keeps whatever they set
			let kept = {};
			try { kept = JSON.parse(search.get('field_filters') || '{}') || {}; } catch (err) { kept = {}; }
			if (kept.item_group) this.fieldFilters.item_group = kept.item_group; else delete this.fieldFilters.item_group;
		}
		if (Object.keys(this.fieldFilters).length) search.set('field_filters', JSON.stringify(this.fieldFilters));
		else search.delete('field_filters');

		if (Object.keys(this.attributeFilters).length) search.set('attribute_filters', JSON.stringify(this.attributeFilters));
		else search.delete('attribute_filters');

		search.delete('start');

		window.history.pushState('filters', '', `${url.pathname}?${search.toString()}`);

		const pv = window.__raftorProductView;
		if (pv && typeof pv.make === 'function') {
			pv.make(true);
		} else {
			window.location.href = url.toString();
		}
		this.render();
		this._closeMobileOffcanvas();
	}

	_clearAll() {
		this.fieldFilters = {};
		this.attributeFilters = {};
		this._writeUrlAndRefresh();
	}

	/* ── mobile off-canvas (both layouts collapse into the same drawer) ──── */

	_setupMobileOffcanvas() {
		if (this._offcanvasReady) return;
		this._offcanvasReady = true;

		const toggle = el('button', {
			type: 'button',
			class: 'rfhq-mobile-filters-toggle',
			text: __('Filters'),
		});
		const overlay = el('div', { class: 'rfhq-mobile-filters-overlay hidden' });

		this.el.parentElement.insertBefore(toggle, this.el);
		document.body.appendChild(overlay);

		toggle.addEventListener('click', () => this._openMobileOffcanvas());
		overlay.addEventListener('click', () => this._closeMobileOffcanvas());

		this._overlay = overlay;
	}

	_openMobileOffcanvas() {
		this.el.classList.add('rfhq-filters-offcanvas-open');
		if (this._overlay) this._overlay.classList.remove('hidden');
		document.body.classList.add('rfhq-filters-open');
	}

	_closeMobileOffcanvas() {
		this.el.classList.remove('rfhq-filters-offcanvas-open');
		if (this._overlay) this._overlay.classList.add('hidden');
		document.body.classList.remove('rfhq-filters-open');
	}
}

/**
 * The filter panel for a grid an app feeds (the "App Listing" source of an Item Grid): its facets come from the app's answer (source.filters), a choice
 * goes to the same source as plain values and the same grid is drawn again. Always the horizontal layout, above the grid.
 */
export function mountAppFilters(section, grid) {
	const source = grid && grid.source;
	if (!source || !Array.isArray(source.filters) || !source.filters.length || section.querySelector('.rfhq-catalog-filters')) return;
	// Listings of the same records on one page share one set of buttons, shown above the first of them; the others follow it (rfhq_templates.js).
	const same = Array.from(document.querySelectorAll('[data-source="App Listing"]')).filter((x) => x.dataset.recordsSource === section.dataset.recordsSource);
	if (same.length && same[0] !== section) return;
	// The kinds a list is looked at in (tabs or tiles) stand on their own above the list, always in view; the other buttons follow, in the panel that opens when there are many.
	const isTop = (facet) => facet.display === 'tabs' || facet.display === 'tiles';
	const hosts = [];
	const draw = (pick, anchor) => {
		const own = source.filters.filter(pick);
		if (!own.length) return;
		const host = el('div', { class: 'rfhq-app-filters' });
		section.insertBefore(host, anchor || section.firstChild);  // above the section's own heading and cards, so the buttons read as the start of what follows
		const filters = new RfhqCatalogFilters({
			el: host,
			layout: 'horizontal',
			facets: own,
			onChange: () => {  // what is chosen is in the address, for every set of buttons on the page
				const query = new URLSearchParams(window.location.search);
				source.args = Object.assign({}, source.args);
				source.filters.forEach((facet) => { if (query.get(facet.name)) source.args[facet.name] = query.get(facet.name); else delete source.args[facet.name]; });
				grid.reset();
			},
		});
		host.setAttribute('data-rfhq-owned', '1');
		// The app may answer other buttons after a choice (the topics of the kind just chosen): the list asks for them to be drawn again.
		host._rfhqRefresh = (facets) => { source.filters = facets; filters.appFacets = facets.filter(pick); filters.render(); };
		filters.render();
		if (window.rfhqApplyOwnedThemeFallback) window.rfhqApplyOwnedThemeFallback(host);
		hosts.push(host);
		return host;
	};
	const isChips = (facet) => facet.display === 'chips' || Boolean(facet.toggle);  // a switch such as "online now" sits with the pills
	const top = draw(isTop);
	const chips = draw(isChips, top ? top.nextSibling : null);  // the pills that narrow the list stay in view under the kinds
	draw((facet) => !isTop(facet) && !isChips(facet), chips ? chips.nextSibling : top ? top.nextSibling : null);
}
